/** 与 api/models/roast/recipe_bounds.go 默认规则保持一致，用于前端展示偏移 */

const DEFAULT_TEMP_DELTA = 15
const DEFAULT_TIME_PCT = 0.2
const DEFAULT_TIME_MIN_SEC = 30
const DEFAULT_AGTRON_DELTA = 8
const DEFAULT_LOSS_DELTA = 5
const DEFAULT_BEAN_QTY_PCT = 0.15
const DEFAULT_BEAN_QTY_MIN = 50

function boundsUnset(lower, upper) {
	return (lower || 0) === 0 && (upper || 0) === 0
}

function fillTempBound(recipe, lowerKey, upperKey, nominalKey, absMin, absMax) {
	if (!boundsUnset(recipe[lowerKey], recipe[upperKey])) return
	const nominal = recipe[nominalKey] || 0
	if (nominal > 0) {
		recipe[lowerKey] = Math.max(0, nominal - DEFAULT_TEMP_DELTA)
		recipe[upperKey] = nominal + DEFAULT_TEMP_DELTA
	} else {
		recipe[lowerKey] = absMin
		recipe[upperKey] = absMax
	}
}

function fillFloatBound(recipe, lowerKey, upperKey, nominalKey, absMin, absMax) {
	if (!boundsUnset(recipe[lowerKey], recipe[upperKey])) return
	const nominal = recipe[nominalKey] || 0
	if (nominal > 0) {
		recipe[lowerKey] = Math.max(0, nominal - DEFAULT_LOSS_DELTA)
		recipe[upperKey] = nominal + DEFAULT_LOSS_DELTA
	} else {
		recipe[lowerKey] = absMin
		recipe[upperKey] = absMax
	}
}

function fillTimeBound(recipe, lowerKey, upperKey, nominalKey, absMin, absMax) {
	if ((recipe[lowerKey] || 0) !== 0 || (recipe[upperKey] || 0) !== 0) return
	const nominal = recipe[nominalKey] || 0
	if (nominal > 0) {
		const delta = Math.max(DEFAULT_TIME_MIN_SEC, Math.floor(nominal * DEFAULT_TIME_PCT))
		recipe[lowerKey] = nominal > delta ? nominal - delta : 0
		recipe[upperKey] = nominal + delta
	} else {
		recipe[lowerKey] = absMin
		recipe[upperKey] = absMax
	}
}

function fillAgtronBound(recipe, lowerKey, upperKey, nominalKey, absMin, absMax) {
	if ((recipe[lowerKey] || 0) !== 0 || (recipe[upperKey] || 0) !== 0) return
	const nominal = recipe[nominalKey] || 0
	if (nominal > 0) {
		recipe[lowerKey] = Math.max(0, nominal - DEFAULT_AGTRON_DELTA)
		recipe[upperKey] = nominal + DEFAULT_AGTRON_DELTA
	} else {
		recipe[lowerKey] = absMin
		recipe[upperKey] = absMax
	}
}

function fillBeanQtyBound(recipe) {
	if ((recipe.beanqty_lower || 0) !== 0 || (recipe.beanqty_upper || 0) !== 0) return
	const nominal = recipe.beanqty || 0
	if (nominal > 0) {
		const delta = Math.max(DEFAULT_BEAN_QTY_MIN, Math.floor(nominal * DEFAULT_BEAN_QTY_PCT))
		recipe.beanqty_lower = Math.max(0, nominal - delta)
		recipe.beanqty_upper = nominal + delta
	} else {
		recipe.beanqty_lower = 100
		recipe.beanqty_upper = 5000
	}
}

export function ensureRecipeBounds(recipe) {
	if (!recipe) return recipe
	const r = { ...recipe }
	fillTempBound(r, 'starttemp_lower', 'starttemp_upper', 'starttemp', 160, 250)
	fillTempBound(r, 'tptemp_lower', 'tptemp_upper', 'tptemp', 80, 160)
	fillTempBound(r, 'yellowtemp_lower', 'yellowtemp_upper', 'yellowtemp', 120, 200)
	fillTempBound(r, 'fctemp_lower', 'fctemp_upper', 'fctemp', 150, 230)
	fillTempBound(r, 'sctemp_lower', 'sctemp_upper', 'sctemp', 170, 250)
	fillTimeBound(r, 'tptime_lower', 'tptime_upper', 'tptime', 30, 600)
	fillTimeBound(r, 'yellowtime_lower', 'yellowtime_upper', 'yellowtime', 120, 900)
	fillTimeBound(r, 'fctime_lower', 'fctime_upper', 'fctime', 300, 1200)
	fillTimeBound(r, 'sctime_lower', 'sctime_upper', 'sctime', 0, 1800)
	fillTimeBound(r, 'totaltime_lower', 'totaltime_upper', 'totaltime', 480, 1800)
	fillFloatBound(r, 'lossrate_lower', 'lossrate_upper', 'lossrate', 5, 25)
	fillAgtronBound(r, 'agtron_lower', 'agtron_upper', 'agtron', 40, 120)
	fillAgtronBound(r, 'agtron_ground_lower', 'agtron_ground_upper', 'agtron_ground', 30, 110)
	fillBeanQtyBound(r)
	return r
}

/** 合并已完成烘焙的最终参数（与后端 completedRoastMatchSnapshot 一致） */
export function buildRoastMatchSnapshot(roast, profile) {
	const snap = { ...(profile || {}) }
	if (!roast) return snap
	if (!snap.bean_id && roast.bean_id) snap.bean_id = roast.bean_id
	if (roast.degree) snap.degree = roast.degree
	if (roast.agtron) snap.agtron = roast.agtron
	if (roast.agtron_ground) snap.agtron_ground = roast.agtron_ground
	if (roast.lossrate) {
		snap.lossrate = roast.lossrate
	} else if (roast.inqty > 0 && roast.outqty > 0) {
		snap.lossrate = Number((((roast.inqty - roast.outqty) / roast.inqty) * 100).toFixed(1))
	}
	return snap
}

const PARAM_SPECS = [
	{ key: 'beanqty', label: '入豆量(g)', roastKey: 'inqty', fromRoast: true, unit: 'g', int: true },
	{ key: 'starttemp', label: '入豆温', unit: '°C' },
	{ key: 'tptemp', label: '回温温度', unit: '°C' },
	{ key: 'tptime', label: '回温时间', unit: 's', int: true },
	{ key: 'yellowtemp', label: '转黄温度', unit: '°C' },
	{ key: 'yellowtime', label: '转黄时间', unit: 's', int: true },
	{ key: 'fctemp', label: '一爆温度', unit: '°C' },
	{ key: 'fctime', label: '一爆时间', unit: 's', int: true },
	{ key: 'sctemp', label: '二爆温度', unit: '°C' },
	{ key: 'sctime', label: '二爆时间', unit: 's', int: true },
	{ key: 'totaltime', label: '总时间', unit: 's', int: true },
	{ key: 'lossrate', label: '失重率', unit: '%' },
	{ key: 'agtron', label: 'Agtron豆', unit: '', int: true },
	{ key: 'agtron_ground', label: 'Agtron粉', unit: '', int: true }
]

function readActual(spec, roast, profile) {
	if (spec.fromRoast) {
		const v = roast?.[spec.roastKey]
		return v == null || v === '' ? null : Number(v)
	}
	const v = profile?.[spec.key]
	return v == null || v === '' || v === 0 ? null : Number(v)
}

function formatNum(v, int) {
	if (v == null || Number.isNaN(v)) return '—'
	return int ? String(Math.round(v)) : String(Number(v.toFixed(1)))
}

/**
 * 雷达图按配方上下限相对标称的偏移百分比做归一化：
 * 中点(50)=标称；内端(0)=下限；外端(100)=上限；超出夹到两端。
 * 上下半宽可非对称（例如下限 -8%、上限 +12%）。
 */
function toRadarPosByBounds(actual, center, lower, upper) {
	if (!(center > 0)) return 50

	const lowerSpan = center - lower
	const upperSpan = upper - center
	const delta = actual - center

	let pos
	if (delta >= 0) {
		const span = upperSpan > 0 ? upperSpan : center // 无有效上限时退回按标称 100%
		pos = 50 + (delta / span) * 50
	} else {
		const span = lowerSpan > 0 ? lowerSpan : center
		pos = 50 + (delta / span) * 50 // delta 为负，向 0 靠近
	}
	return Math.max(0, Math.min(100, pos))
}

/** 相对上下限的归一化偏移：0%=标称，±100%=落在对应上下限，可超过后夹紧显示 */
function formatRangeNormalizedPercent(actual, center, lower, upper) {
	if (!(center > 0)) return null
	const delta = actual - center
	let ratio
	if (delta >= 0) {
		const span = upper - center
		if (!(span > 0)) return formatDeltaPercent(delta, center)
		ratio = delta / span
	} else {
		const span = center - lower
		if (!(span > 0)) return formatDeltaPercent(delta, center)
		ratio = delta / span // 负向，如 -0.67
	}
	const pct = ratio * 100
	return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`
}

function formatRadarTolerance(center, lower, upper, int, unit) {
	if (!(center > 0)) return '中点=标称，两端=上下限'
	const lowPct = ((lower - center) / center) * 100
	const upPct = ((upper - center) / center) * 100
	const lowTxt = `${lowPct >= 0 ? '+' : ''}${lowPct.toFixed(1)}%`
	const upTxt = `${upPct >= 0 ? '+' : ''}${upPct.toFixed(1)}%`
	return `中点=标称；两端 ${lowTxt} ~ ${upTxt}（${formatNum(lower, int)}~${formatNum(upper, int)}${unit}）`
}

function formatDeltaPercent(delta, nominal) {
	if (!nominal || nominal === 0) return null
	return `${delta >= 0 ? '+' : ''}${((delta / nominal) * 100).toFixed(1)}%`
}

/**
 * @returns {{ offsets: Array, indicators: Array, recipeSeries: number[], roastSeries: number[] }}
 */
export function computeRecipeOffsets(recipe, roast, profile) {
	const bounds = ensureRecipeBounds(recipe)
	const snap = buildRoastMatchSnapshot(roast, profile)
	const offsets = []

	for (const spec of PARAM_SPECS) {
		const actual = readActual(spec, roast, snap)
		if (actual == null) continue

		const nominal = Number(bounds[spec.key]) || 0
		const lower = Number(bounds[`${spec.key}_lower`]) || 0
		const upper = Number(bounds[`${spec.key}_upper`]) || 0
		if (lower === 0 && upper === 0 && nominal === 0) continue

		const center = nominal > 0 ? nominal : (lower + upper) / 2
		if (!(center > 0)) continue

		const delta = actual - center
		const inRange = actual >= lower && actual <= upper
		const radarRecipe = 50
		const radarRoast = toRadarPosByBounds(actual, center, lower, upper)
		const deltaPct = formatDeltaPercent(delta, center)
		const rangePct = formatRangeNormalizedPercent(actual, center, lower, upper)
		const deltaAbsText = `${delta >= 0 ? '+' : ''}${formatNum(delta, spec.int)}${spec.unit}`

		offsets.push({
			key: spec.key,
			label: spec.label,
			actual,
			nominal: center,
			lower,
			upper,
			delta,
			deltaPct,
			rangePct,
			deltaAbsText,
			deltaText: deltaPct ? `${deltaAbsText}（${deltaPct}）` : deltaAbsText,
			actualText: `${formatNum(actual, spec.int)}${spec.unit}`,
			nominalText: `${formatNum(center, spec.int)}${spec.unit}`,
			rangeText: `${formatNum(lower, spec.int)} ~ ${formatNum(upper, spec.int)}${spec.unit}`,
			displayTolerance: formatRadarTolerance(center, lower, upper, spec.int, spec.unit),
			inRange,
			radarRecipe,
			radarRoast
		})
	}

	return {
		offsets,
		indicators: offsets.map((o) => ({ name: o.label, max: 100 })),
		recipeSeries: offsets.map((o) => o.radarRecipe),
		roastSeries: offsets.map((o) => o.radarRoast)
	}
}
